import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  CAB_SHARES, MY_CAB_SHARES, CREATE_CAB_SHARE, JOIN_CAB_SHARE,
  LEAVE_CAB_SHARE, UPDATE_CAB_STATUS,
} from '../graphql';
import {
  Car, Clock, Users, Plus, X, ArrowRight, Phone, StickyNote,
  LogOut, Loader2, AlertCircle,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const STATUSES = [
  { value: 'OPEN',      label: 'Open',      color: 'bg-green-100 text-green-700' },
  { value: 'FULL',      label: 'Full',      color: 'bg-amber-100 text-amber-700' },
  { value: 'DEPARTED',  label: 'Departed',  color: 'bg-blue-100 text-blue-700' },
  { value: 'CANCELLED', label: 'Cancelled', color: 'bg-gray-100 text-gray-500' },
];

// Host actions available from each status
const HOST_ACTIONS = {
  OPEN:      [{ to: 'DEPARTED', label: 'Mark departed' }, { to: 'CANCELLED', label: 'Cancel' }],
  FULL:      [{ to: 'DEPARTED', label: 'Mark departed' }, { to: 'CANCELLED', label: 'Cancel' }],
  DEPARTED:  [],
  CANCELLED: [{ to: 'OPEN', label: 'Reopen' }],
};

const toDate = (v) => {
  if (!v) return null;
  const n = Number(v);
  const d = new Date(Number.isNaN(n) ? v : n);
  return Number.isNaN(d.getTime()) ? null : d;
};

const fmtDateTime = (v) => {
  const d = toDate(v);
  return d ? d.toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' }) : '—';
};

const isPast = (v) => {
  const d = toDate(v);
  return d ? d.getTime() < Date.now() : false;
};

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

function StatusBadge({ status }) {
  const cfg = STATUSES.find(s => s.value === status) || { label: status, color: 'bg-gray-100 text-gray-500' };
  return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cfg.color}`}>{cfg.label}</span>;
}

function RideCard({ ride, currentUserId, onJoin, onLeave, onStatus, busy }) {
  const isHost = String(ride.creator?.id) === String(currentUserId);
  const seatsLeft = Math.max(0, (ride.availableSeats || 0) - (ride.memberCount || 0));
  const departed = isPast(ride.departureTime);
  const canJoin = !ride.isMember && !isHost && ride.status === 'OPEN' && seatsLeft > 0;
  const [showMembers, setShowMembers] = useState(false);

  return (
    <div className="card !p-5 hover:shadow-md transition-shadow">
      {/* Top row: route + status */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-brand-100 flex items-center justify-center text-brand-600 flex-shrink-0">
            <Car size={18} />
          </div>
          <div className="min-w-0">
            <h3 className="font-bold text-gray-900 leading-tight flex items-center gap-1.5 flex-wrap">
              <span className="truncate">{ride.fromLocation}</span>
              <ArrowRight size={14} className="text-brand-400 flex-shrink-0" />
              <span className="truncate">{ride.toLocation}</span>
            </h3>
            <p className="text-xs text-gray-400">Hosted by {isHost ? 'you' : ride.creator?.name}</p>
          </div>
        </div>
        <StatusBadge status={ride.status} />
      </div>

      {/* Details */}
      <div className="space-y-1.5 text-sm text-gray-600 mb-3">
        <p className={`flex items-center gap-1.5 ${departed ? 'text-red-500 font-medium' : ''}`}>
          <Clock size={14} className={departed ? 'text-red-400' : 'text-gray-400'} />
          Departs {fmtDateTime(ride.departureTime)}{departed && <span className="text-xs bg-red-50 text-red-500 px-2 py-0.5 rounded-full">departure passed</span>}
        </p>
        <div className="flex items-center gap-4 flex-wrap">
          <button onClick={() => setShowMembers(s => !s)} className="flex items-center gap-1.5 text-gray-600 hover:text-brand-600 transition-colors">
            <Users size={14} className="text-gray-400" />
            {seatsLeft > 0
              ? <>{seatsLeft} seat{seatsLeft === 1 ? '' : 's'} left <span className="text-xs text-gray-400">({ride.memberCount}/{ride.availableSeats})</span></>
              : <span className="text-amber-600 font-medium">No seats left</span>}
          </button>
          {ride.farePerPerson != null && (
            <span className="text-sm font-semibold text-brand-700 bg-brand-50 px-2.5 py-0.5 rounded-full">{inr(ride.farePerPerson)} / person</span>
          )}
        </div>
        {ride.contact && (
          <p className="flex items-center gap-1.5"><Phone size={14} className="text-gray-400" /> {ride.contact}</p>
        )}
        {ride.notes && <p className="flex items-start gap-1.5 text-gray-500"><StickyNote size={14} className="text-gray-400 mt-0.5 flex-shrink-0" /> {ride.notes}</p>}
      </div>

      {/* Members */}
      {showMembers && ride.members?.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-1.5 bg-gray-50 rounded-xl p-3">
          {ride.members.map(m => (
            <span key={m.id} className="flex items-center gap-1.5 text-xs bg-white border border-gray-100 rounded-full pl-1 pr-2.5 py-0.5">
              <span className="w-5 h-5 rounded-full bg-brand-100 flex items-center justify-center text-brand-700 font-bold">{m.user?.name?.[0]?.toUpperCase()}</span>
              <span className="text-gray-600">{String(m.user?.id) === String(currentUserId) ? 'You' : m.user?.name}</span>
            </span>
          ))}
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-gray-50">
        {canJoin && (
          <button onClick={() => onJoin(ride.id)} disabled={busy} className="btn-primary text-xs flex items-center gap-1"><Plus size={13} /> Join ride</button>
        )}
        {ride.isMember && !isHost && (
          <button onClick={() => onLeave(ride.id)} disabled={busy} className="flex items-center gap-1 text-xs font-medium text-red-500 hover:text-red-700 px-3 py-1.5 rounded-full border border-red-100 hover:border-red-300 transition-colors">
            <LogOut size={13} /> Leave
          </button>
        )}
        {ride.isMember && !isHost && <span className="text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full">You're in</span>}
        {isHost && (HOST_ACTIONS[ride.status] || []).map(a => (
          <button key={a.to} onClick={() => onStatus(ride.id, a.to)} disabled={busy}
            className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${a.to === 'CANCELLED' ? 'text-red-500 border-red-100 hover:border-red-300' : 'text-brand-600 border-brand-100 hover:border-brand-300'}`}>
            {a.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function CabSharePage() {
  const { user } = useAuth();
  const [tab, setTab] = useState('open');            // 'open' | 'mine'
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ fromLocation: '', toLocation: '', departureTime: '', availableSeats: '', farePerPerson: '', notes: '', contact: '' });
  const [cabErr, setCabErr] = useState('');

  const openQ = useQuery(CAB_SHARES, { skip: tab !== 'open' });
  const mineQ = useQuery(MY_CAB_SHARES, { skip: tab !== 'mine' });

  const refetchAll = [{ query: CAB_SHARES }, { query: MY_CAB_SHARES }];

  const [create, { loading: creating }] = useMutation(CREATE_CAB_SHARE, {
    refetchQueries: refetchAll,
    onCompleted: () => { setShowCreate(false); setForm({ fromLocation: '', toLocation: '', departureTime: '', availableSeats: '', farePerPerson: '', notes: '', contact: '' }); setCabErr(''); },
    onError: (e) => setCabErr(e.message?.includes('authenticated') ? 'Session expired — please log in again.' : e.message),
  });
  const [join, { loading: joining }] = useMutation(JOIN_CAB_SHARE, { refetchQueries: refetchAll });
  const [leave, { loading: leaving }] = useMutation(LEAVE_CAB_SHARE, { refetchQueries: refetchAll });
  const [setStatus, { loading: statusing }] = useMutation(UPDATE_CAB_STATUS, { refetchQueries: refetchAll });
  const busy = joining || leaving || statusing;

  const active = tab === 'open' ? openQ : mineQ;
  const rides = tab === 'open' ? (openQ.data?.cabShares || []) : (mineQ.data?.myCabShares || []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.fromLocation.trim() || !form.toLocation.trim()) { setCabErr('From and To locations are required'); return; }
    if (!form.departureTime) { setCabErr('Departure time is required'); return; }
    const dt = new Date(form.departureTime);
    if (isNaN(dt.getTime())) { setCabErr('Invalid departure time'); return; }
    setCabErr('');
    await create({ variables: {
      fromLocation: form.fromLocation,
      toLocation: form.toLocation,
      departureTime: dt.toISOString(),
      availableSeats: form.availableSeats ? parseInt(form.availableSeats, 10) : null,
      farePerPerson: form.farePerPerson ? parseFloat(form.farePerPerson) : null,
      notes: form.notes || null,
      contact: form.contact || null,
    } });
  };

  return (
    <div className="max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Cab Share</h1>
          <p className="text-gray-500 text-sm">Split cab fares with students on your route · Airport runs, station drops, city trips</p>
        </div>
        <button onClick={() => setShowCreate(true)} className="btn-primary flex items-center gap-1.5 text-sm"><Plus size={14} /> Offer a ride</button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4">
        {[{ k: 'open', label: 'Open rides' }, { k: 'mine', label: 'Mine' }].map(t => (
          <button key={t.k} onClick={() => setTab(t.k)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${tab === t.k ? 'bg-gray-800 text-white border-transparent' : 'border-gray-200 text-gray-500 hover:border-gray-400'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* States */}
      {active.loading && (
        <div className="flex items-center justify-center gap-2 py-16 text-gray-400"><Loader2 size={18} className="animate-spin" /> Loading rides…</div>
      )}
      {active.error && !active.loading && (
        <div className="card !p-5 flex items-center gap-3 text-sm text-red-600 bg-red-50">
          <AlertCircle size={18} className="flex-shrink-0" />
          <span>Couldn't load cab shares — {active.error.message}</span>
          <button onClick={() => active.refetch()} className="ml-auto text-xs font-semibold underline">Retry</button>
        </div>
      )}
      {!active.loading && !active.error && rides.length === 0 && (
        <div className="text-center py-16">
          <Car size={48} className="mx-auto mb-3 text-gray-300" />
          <p className="text-gray-500">{tab === 'mine' ? "You haven't joined any rides yet." : 'No rides posted right now.'}</p>
          <button onClick={() => setShowCreate(true)} className="btn-primary mt-3 text-sm">+ Offer a ride</button>
        </div>
      )}

      {/* Grid */}
      {!active.loading && !active.error && rides.length > 0 && (
        <div className="grid gap-4 md:grid-cols-2">
          {rides.map(r => (
            <RideCard key={r.id} ride={r} currentUserId={user?.id} busy={busy}
              onJoin={cabId => join({ variables: { cabId } })}
              onLeave={cabId => leave({ variables: { cabId } })}
              onStatus={(cabId, status) => setStatus({ variables: { cabId, status } })} />
          ))}
        </div>
      )}

      {/* Create modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-100 sticky top-0 bg-white">
              <h2 className="font-bold text-gray-900">Offer a ride</h2>
              <button onClick={() => setShowCreate(false)}><X size={18} className="text-gray-400" /></button>
            </div>
            <form onSubmit={handleCreate} className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <input required className="input w-full" placeholder="From (e.g. Campus gate)" value={form.fromLocation} onChange={e => setForm(f => ({ ...f, fromLocation: e.target.value }))} />
                <input required className="input w-full" placeholder="To (e.g. Airport T1)" value={form.toLocation} onChange={e => setForm(f => ({ ...f, toLocation: e.target.value }))} />
              </div>
              <div>
                <label className="text-xs text-gray-400 block mb-1">Departure time</label>
                <input required className="input w-full" type="datetime-local" value={form.departureTime} onChange={e => setForm(f => ({ ...f, departureTime: e.target.value }))} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input className="input w-full" type="number" min="1" placeholder="Seats (default 3)" value={form.availableSeats} onChange={e => setForm(f => ({ ...f, availableSeats: e.target.value }))} />
                <input className="input w-full" type="number" min="0" step="0.01" placeholder="Fare per person (₹)" value={form.farePerPerson} onChange={e => setForm(f => ({ ...f, farePerPerson: e.target.value }))} />
              </div>
              <input className="input w-full" placeholder="Contact (phone / WhatsApp, optional)" value={form.contact} onChange={e => setForm(f => ({ ...f, contact: e.target.value }))} />
              <textarea className="input w-full resize-none h-20 text-sm" placeholder="Notes (luggage space, pickup point…)" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
              {cabErr && <p className="text-sm text-red-500">{cabErr}</p>}
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowCreate(false)} className="btn-secondary flex-1">Cancel</button>
                <button type="submit" disabled={creating} className="btn-primary flex-1">{creating ? 'Posting…' : 'Post ride'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
